<template>

  <div class="py-4">
    <HeaderBreadcrumbs :items="[
      { label: 'Books', to: '/ebooks' },
      { label: `Bulk Upload File  ${this.slug}` }
    ]" :title="`Bulk Upload File ${this.slug}`" />

    <div v-if="this.files.length === 0" class="bg-white rounded-md shadow-sm flex-col w-full">

      <input ref="fileInput" type="file" multiple accept=".pdf,.epub" style="display:none" @change="handleFiles">
      <div class="flex justify-between w-full">
        <div class="px-4 mt-4">
          <h2 class="text-base font-semibold text-gray-900  mb-0"> Upload your files</h2>
          <p class="text-gray-500 text-sm pt-0 mb-0 leading-8">Select or drag one or more files into the box to start
            uploading.</p>
        </div>
        <div :class="{ 'disabled opacity-50 pointer-events-none': profile?.item_id?.role_id === 5 }" class="px-4 mt-4">
          <router-link class="text-gray-500 hover:no-underline hover:text-gray-500"
            :to="`/items/bulk/log_activity/${slug}`">
            <Button class="flex justify-items-start items-center" variant="outlined" color="alternative" size="xs">
              <svg class="w-4 h-4 mr-1 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M18 14v4.833A1.166 1.166 0 0 1 16.833 20H5.167A1.167 1.167 0 0 1 4 18.833V7.167A1.166 1.166 0 0 1 5.167 6h4.618m4.447-2H20v5.768m-7.889 2.121 7.778-7.778" />
              </svg>
              View History
            </Button>
          </router-link>
        </div>
      </div>
      <div class="flex justify-start w-full px-4 py-3">
        <div class="mr-2.5" v-if="this.files.length === 0">
          <div class="bg-gray-50 dropzone-style justify-center items-center flex" @dragover.prevent @dragenter.prevent
            @drop.prevent="handleDrop">

            <div class="dropzone-content flex justify-center items-center flex-col">

              <div class="icon mb-2">
                <svg class="w-7 h-7 text-gray-500 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                  width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                  <path fill-rule="evenodd"
                    d="M12 3a1 1 0 0 1 .78.375l4 5a1 1 0 1 1-1.56 1.25L13 6.85V14a1 1 0 1 1-2 0V6.85L8.78 9.626a1 1 0 1 1-1.56-1.25l4-5A1 1 0 0 1 12 3ZM9 14v-1H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-4v1a3 3 0 1 1-6 0Zm8 2a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2H17Z"
                    clip-rule="evenodd" />
                </svg>

              </div>
              <p class="text-sm text-gray-500 m-0">
                Drag & drop files PDF or EPUB
              </p>
              <a class="text-xs text-blue-600 underline" href="#" @click.prevent="$refs.fileInput.click()">
                or browse for files
              </a>

              <input ref="fileInput" type="file" multiple accept=".pdf,.epub" hidden @change="handleFiles" />

            </div>

          </div>
        </div>

        <div class='w-full ml-2.5 ' v-if="this.files.length === 0">
          <div class="bg-blue-custom bg-blue-50 rounded-xl p-0 text-blue-900 max-w-2xl">
            <div class="flex items-start gap-0 rounded-lg p-3">
              <!-- Icon -->
              <img src="../../assets/img/info.svg" class="w-4 h-5 mr-1 mt-1" />
              <div>
                <!-- Title -->
                <p class="font-semibold ml-1 text-lg mb-0">
                  Before you upload. Make matching easier.
                </p>
                <!-- List -->
                <ol class="list-decimal ml-0 space-y-1 mb-0 text-sm">
                  <li>
                    Rename your files using the
                    <span class="font-semibold">Content ID</span>
                    to make matching faster.
                  </li>
                  <li>
                    Check file size — max
                    <span class="font-semibold">100 MB</span>.
                  </li>

                  <li>
                    Supported formats:
                    <span class="font-semibold">PDF</span> and
                    <span class="font-semibold">EPUB</span> only.
                  </li>

                  <li>
                    Maximum upload:
                    <span class="font-semibold">20 files</span> at once.
                  </li>
                </ol>
              </div>

            </div>
          </div>
        </div>
      </div>


    </div>
    <div v-else>
      <div class="flex-col justify-start items-center p-4 bg-white shadow-md rounded-xl">
        <div class="mt-0">
          <h2 class="text-base font-semibold text-gray-900  mb-0">Double-Check File Name and ID</h2>
          <p class="text-gray-500 text-sm pt-0 mb-3 leading-8">Double-check file names and select the correct Content ID
            for each file to ensure accurate updates.</p>
        </div>
        <div class="w-full flex-col border rounded-xl">
          <Table class="table-bulk" v-if="files.length > 0" :headers="headers" :data="this.files" :withAction="true"
            :withNumberCol="false" :row-key="['name_file', 'size', 'format', 'content_id']" :current-page="currentPage"
            :isLoading="false" :per-page="20">
            <template #name_file="{ value }">
              <p class="w-80">{{ value }}</p>
            </template>
            <template #content_id="{ row }">
              <div class="flex justify-start items-baseline w-full">
                <div class="block w-full">
                   <ContentDropdown :items="contentList" :selected="row.matched_content" :error="!!row.error_content_id"
                @select="updateContent(row, $event)" @search="searchContent" />

              <p class="mt-2 text-xs text-red-500 lowercase" v-if="row.error_content_id">
                {{ row.error_content_id }}
              </p>
                </div>
                 <div class="w-auto relative top-1 left-9" @click="removeFile(row)">
                <svg class="w-5 h-5 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                  width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                </svg>

              </div>
              </div>
             
               
            </template>
          </Table>
        </div>
      </div>
    </div>
    <div v-if="this.files.length > 0" class="flex justify-start space-x-4 mt-8 p-4 items-center bg-white rounded-lg">
      <Button :class="{ 'opacity-50': isSubmitting }" :disabled="isSubmitting" @click="openPopup"
        class="flex justify-items-start items-center" variant="default" color="primary" size="sm">
        {{ isSubmitting ? 'Loading...' : 'Submit' }}
      </Button>
      <Button @click="cancel" class="flex justify-items-start items-center" variant="outlined" color="alternative"
        size="sm">
        Cancel
      </Button>
    </div>
    <t-modal ref="confirmationModal">
      <div class="text-sm flex-col text-center">
        <label class="text-center font-semibold">Do you want to continue?</label>
        <p class="text-center text-gray-500">This action will update book content.</p>
      </div>
      <div class="flex justify-center space-x-3">
        <Button @click="uploadAll" class="flex justify-items-start items-center" variant="default" color="primary"
          size="xs">
          Continue
        </Button>
        <Button @click="cancelpopup" class="flex justify-items-start items-center" variant="outlined"
          color="alternative" size="xs">
          Cancel
        </Button>
      </div>
    </t-modal>
    <div v-if="showToast" class="fixed top-20 left-1/2 transform -translate-x-1/2 
         bg-gray-800 text-white px-6 py-3 rounded-xl 
         shadow-lg flex items-center justify-between toast-width z-50
         transition-all duration-300 translate-y-0 opacity-100">
      <span>Upload in progress. Please wait.</span>

      <Button variant="outlined" class="hover:text-gray-800" color="alternative" size="xs"
        @click="$router.push(`/items/bulk/log_activity/${slug}`)">
        View History
      </Button>
    </div>
  </div>

</template>

<script>
import { mapActions, mapState } from 'vuex';
import { DetailItems } from '../../router/content';
import Table from '@/components/form/table.vue'
import { EventBus } from '../../../src/event-bus';
import ContentDropdown from '@/components/form/ContentDropdown.vue';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import Button from '@/components/ui/Button';
import { getFileExtension, isDocumentFile, withDocumentMimeType } from '@/plugins/fileType';

export default {
  name: 'UploadBulkNew',
  components: {
    // AttachmentList,
    Button,
    Table,
    ContentDropdown,
    HeaderBreadcrumbs,
  },
  data() {
    return {
      DetailItems,
      statusUpload: 'Uploading',
      indexUpload: 0,
      currentPage: 1,
      perPage: 20,
      requestId: null,
      error_content_id: null, // string | null
      lengthUpload: 0,
      files: [],
      validateDate: false,
      showToast: false,
      isSubmitting: false,
      maxFiles: 20,
      headers: [
        { label: '', key: '', },
        { label: 'FILE NAME', key: 'name_file', style: 'width: 200px' },
        { label: 'Size', key: 'size' },
        { label: 'Format', key: 'format' },
        { label: 'Content ID', key: 'content_id', style: 'width: 700px' },
      ],
      slug: '',
      contentList: [],
      isData: false,
      isUpload: false,
    };
  },
  computed: {
    getTempAttachments() {
      return this.tempAttachments;
    },
    ...mapState({
      isLoading: (state) => state.ebooks.isLoading,
      successMessage: (state) => state.ebooks.successMessage,
      profile: (state) => state.profile,
    }),
    getAttachments() {
      return this.attachments;
    },
  },
  mounted() {
    this.slug = this.$route.params.slug
  },
  created() {
    this.fetchProfile()
  },
  methods: {
    ...mapActions('bulkUpload', ['uploadBulkNew']),
    ...mapActions('ebooks', ['searchBooks']),
    ...mapActions('magazine', ['searchMagazine']),
     ...mapActions('profile', ['fetchProfile']),
   
    searchContent(search) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        let payload = {
          q: search ?? '',
          page: this.currentPage,
          limit: this.perPage,
        };
        if (this.slug === 'books') {
          this.searchBooks(payload)
            .then((response) => {
              this.contentList = response.data.data.rows;
            })
            .catch(() => {
            });
        }
        if (this.slug === 'magazine') {
          this.searchMagazine(payload)
            .then((response) => {
              this.contentList = response.data.data.rows;
            })
            .catch(() => {
            });
        }

      }, 1000);
    },


    async autoMatchContent(fileName) {
      const normalize = (text) => {
        return text
          .toLowerCase()
          .replace(/\.(pdf|epub)$/i, '')
          .replace(/[^a-z0-9 ]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
      }
      const file = normalize(fileName)
      const payload = {
        q: file,
        page: 1,
        limit: 20,
      }
      try {
        let response = null
        if (this.slug === 'books') {
          response = await this.searchBooks(payload)
        } else if (this.slug === 'magazine') {
          response = await this.searchMagazine(payload)
        }
        if (!response) return null
        const rows = response.data.data.rows || []
        const found = rows.find(item => {
          const title = normalize(item.name)
          const id = String(item.id)

          if (file.includes(title)) return true
          if (file.includes(id)) return true

          return false
        })

        if (!found) return null

        if (this.validateRow(found)) {
          return {
            ...found,
            error_content_id: true
          }
        }

        return found
      } catch (err) {
        return null
      }
    },
    cancel() {
      this.files = []
    },
    handleDrop(e) {

      const droppedFiles = Array.from(e.dataTransfer.files)
      const event = {
        target: {
          files: droppedFiles
        }
      }

      this.handleFiles(event)

    },
    generateCode() {
      return crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    },
    async handleFiles(e) {
      const selected = Array.from(e.target.files)
      if (this.files.length + selected.length > this.maxFiles) {
        EventBus.$emit('show-toast', {
          message: 'You’ve selected 20 files. Please remove 5 files to continue (Max: 20)',
        })
        return
      }

      for (const selectedFile of selected) {
        // Validasi dari ekstensi, bukan `file.type` (di Windows sering kosong untuk .epub)
        if (!isDocumentFile(selectedFile)) {
          alert("File harus PDF atau EPUB")
          continue
        }
        const ext = getFileExtension(selectedFile.name)
        const file = withDocumentMimeType(selectedFile)
        const matched = await this.autoMatchContent(file.name)

        this.files.push({
          id: Date.now() + Math.random(),
          file: file,
          name_file: file.name,
          size: this.formatSize(file.size),
          format: ext.toUpperCase(),
          content_id: matched ? matched.id : null,
          matched_content: matched,
          error_content_id: null,
        })
        const last = this.files[this.files.length - 1]
        last.error_content_id = this.validateRow(last)
      }

      e.target.value = null
    },
    validateRow(row) {
      const content = row.matched_content
      const status = (content?.item_status || '').toLowerCase()
      const releaseSchedule = content?.release_schedule
      if (['uploading', 'need approval'].includes(status)) {
        return 'This file is in “Uploading” or “Need Approval” status. Delete this row'
      }

      if (!row.content_id) {
        return 'must be selected.'
      }

      if (!releaseSchedule) {
        return 'release schedule must be today or later. delete this row.'
      }

      const now = new Date()
      const releaseDate = new Date(releaseSchedule)

      if (isNaN(releaseDate) || releaseDate < now) {
        return 'release schedule must be today or later. delete this row.'
      }

      return null
    },
    updateContent(row, item) {
      row.content_id = item.id
      row.matched_content = item

      row.error_content_id = this.validateRow(row)
    },
    async uploadAll() {
      this.isSubmitting = true
      this.$refs.confirmationModal.hide()
      this.requestId = `BUB-${this.generateCode()}`
      const failedFiles = []

      try {
        // Satu file gagal tidak boleh menghentikan file lain
        for (const file of this.files) {
          const isUploaded = await this.uploadFile(file)
          if (!isUploaded) failedFiles.push(file)
        }

        if (!failedFiles.length) {
          this.showUploadToast()
          return
        }

        // Sisakan file yang gagal di tabel beserta pesan error-nya agar bisa dicoba ulang
        this.files = failedFiles
        EventBus.$emit('show-toast', {
          message: `${failedFiles.length} file failed to upload. Please check the list and try again.`,
          type: 'error',
        })
      } finally {
        this.isSubmitting = false
      }
    },
    getUploadErrorMessage(error) {
      const message = error?.data?.message ?? error?.response?.data?.message
      if (typeof message === 'string') return message
      if (typeof message?.message === 'string') return message.message
      return 'upload failed. please try again.'
    },
    showUploadToast() {
      this.showToast = true
      this.files = []
      // optional auto hide
      setTimeout(() => {
        this.showToast = false
      }, 5000)
    },
    openPopup() {
      this.files.forEach(file => {
        file.error_content_id = this.validateRow(file)
      })

      const hasError = this.files.some(file => file.error_content_id)
      if (hasError) return

      this.$refs.confirmationModal.show()
    },
    cancelpopup() {
      this.$refs.confirmationModal.hide();
    },
    async uploadFile(item) {
      if (!item.content_id) {
        item.error_content_id = 'must be selected.'
        return false
      }

      item.error_content_id = null

      const formData = new FormData()
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      const formattedDate =
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
      `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    
      formData.append("item_id", item.content_id)
      formData.append("item_file", item.file)
      formData.append("item_type", this.slug === 'books' ? 'book' : 'magazine')
      formData.append("item_release_schedule", formattedDate)
      formData.append("total_items", this.files.length)
      formData.append("request_id",  this.requestId)
      try {
        await this.uploadBulkNew(formData)
        return true
      } catch (error) {
        item.error_content_id = this.getUploadErrorMessage(error)
        return false
      }
    },
    removeFile(row) {
      const index = this.files.findIndex(f => f.id === row.id)
      if (index !== -1) {
        this.files.splice(index, 1)
      }
    },

    formatSize(bytes) {
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      if (bytes === 0) return '0 Byte'
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)))
      return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i]

    },

  }
}
</script>
<style>
.dropzone-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border: 0px;
  border-radius: 15px;
}

.dropzone-style {
  width: 716px;
  height: 220px;
  border: 2px dashed #E5E7EB;
  border-radius: 16px;
}

.table-bulk {
  margin: 1.5px;
}

.bg-blue-custom {
  min-height: 146px !important;
}

.toast-width {
  width: 500px;
}
</style>
